// @flow
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import createCachedSelector from "re-reselect";

const getTabLists = (tab: string) => (state: Object) => state[tab].lists;

// TODO - dont wrap current context in object - its a new reference every time !!!!!!!!!!!!!!!!
const getTabContext = (tab: string) => (state: Object): {context: number} => ({ context: state[tab].currentContext });

const getMaxData = (tab: string) => (state: Object) => ({
	lists: getTabLists(tab)(state),
	maxAllowed: state.widgetConfig.maxFiles,
	successfulCount: state.widgetData.successfulCount,
});

const getListReachedMaxSelection = (list: Object, maxAllowed: ?number, successCount: number) =>
	!!(maxAllowed && list.selected.length >= (maxAllowed - successCount));

const selectTabReachedMaxSelectionWithContext = (tab: string) => createSelector<Object, *, *, *>(
	getMaxData(tab),
	(data) => (context: any) => getListReachedMaxSelection(data.lists[context], data.maxAllowed, data.successfulCount),
);

const selectTabContextItems = (tab: string) => createSelector<Object, *, *, *>(
	(state: Object) => ({
		lists: getTabLists(tab)(state),
		context: getTabContext(tab)(state).context,
	}),
	({ lists, context }) => lists[context],
);

const selectTabIsIdSelected = (tab: string) => createSelector<Object, *, *, *>(
	getTabLists(tab),
	(lists) => (id: string, context: any) => (lists[context].selected.indexOf(id) > -1),
);


// TODO - update all occurrences that use this selector after unwrapping context with object
const selectTabCurrentContext = (tab: string) => createSelector<Object, *, *, *>(
	getTabContext(tab),
	(context) => context,
);

const getListAvailableUntilMax = (list: Object, maxAllowed: ?number, successCount: number) => (maxAllowed
	? (maxAllowed - successCount - list.selected.length) : Infinity);

const selectTabAvailableUntilMax = (tab: string) => createSelector<Object, *, *, *>(
	getMaxData(tab),
	(data) => (context: any) => getListAvailableUntilMax(data.lists[context], data.maxAllowed, data.successfulCount),
);

const getListMultipleIdsInRange = (list: Object, id: string) : string[] => {
	const lastSelected = list.selected[(list.selected.length - 1)];
	const result = [];

	if (lastSelected) {
		const lastIndex = list.items.findIndex((item) => (item.id === lastSelected));
		const newIndex = list.items.findIndex((item) => (item.id === id));

		if (lastIndex === newIndex) {
			result.push(id); // could be user clicked the same item already selected
		} else {
			const goUp = (lastIndex < newIndex);

			// either go up from last or go down based on the new index
			for (let i = (lastIndex + (goUp ? 1 : -1)); (goUp ? i <= newIndex : i >= newIndex); (goUp ? i++ : i--)) {
				result.push(list.items[i].id);
			}
		}
	}

	return result;
};

const selectTabMultipleIdsInRange = (tab: string) => createSelector<Object, *, *, *>(
	getTabLists(tab),
	(lists) => (id: string, context: any) => getListMultipleIdsInRange(lists[context], id),
);

const selectSearchParams = (state, source) => ({
	searchPhrase: state[source].searchPhrase,
	mediaType: state[source].mediaType,
	sortOrder: state[source].sortOrder,
});
const memoizedSelectSearchParams = createCachedSelector([selectSearchParams], (props) => ({ ...props }))((state, source) => source);
const useSearchParams = (source: string) => useSelector((state) => memoizedSelectSearchParams(state, source));

const selectFilterParams = (state, source) => ({
	...state[source].filters
});
const memoizedSelectFilterParams = createCachedSelector([selectFilterParams], (props) => ({ ...props }))((state, source) => source);
const useFilterParams = (source: string) => useSelector((state) => memoizedSelectFilterParams(state, source));


const selectAuth = (state: GettyState, source: string) => ({ isSignedIn: state[source].isSignedIn, isAuthInProgress: state[source].isAuthInProgress });
const memoizedSelectAuth = createCachedSelector([selectAuth], (props) => ({ ...props }))((state, source) => source);
const useAuth = (source: string) => useSelector((state) => memoizedSelectAuth(state, source));


const selectUI = (state: GettyState, source: string) => ({
	isFiltersPanelOpen: state[source].isFiltersPanelOpen,
	areFiltersSet: state[source].areFiltersSet,
	purchaseInProgress: state[source].purchaseInProgress,
});
const memoizedSelectUI = createCachedSelector([selectUI], (props) => ({ ...props }))((state, source) => source);
const useUI = (source: string) => useSelector((state) => memoizedSelectUI(state, source));


const selectAssets = (state: GettyState, source: string) => ({
	items: state[source].list.items,
	assetsInProgress: state[source].list.assetsInProgress,
	page: state[source].list.page,
	hasMoreAssets: state[source].list.hasMoreAssets,
	selected: state[source].list.selected,
});
const memoizedSelectAssets = createCachedSelector([selectAssets], (props) => ({ ...props }))((state, source) => source);
const useAssets = (source: string) => useSelector((state) => memoizedSelectAssets(state, source));

export {
	getTabLists,
	getTabContext,
	getListMultipleIdsInRange,
	getListReachedMaxSelection,
	getListAvailableUntilMax,
	selectTabReachedMaxSelectionWithContext,
	selectTabContextItems,
	selectTabIsIdSelected,
	selectTabCurrentContext,
	selectTabAvailableUntilMax,
	selectTabMultipleIdsInRange,
	useSearchParams,
	selectSearchParams,
	useFilterParams,
	selectFilterParams,
	useAuth,
	selectAuth,
	useAssets,
	selectAssets,
	useUI,
	selectUI,
};
